import { z } from 'zod';
import { JsonSafeMetadataZod } from './json.safe.metadata.zod';
import { JsonValueZod } from './json.value.zod';
import { ValidatorRefZod } from './validator.ref.zod';

// Per-field model-write debounce. Angular Signal Forms can delay by duration or
// buffer continuous input until the control reports touch on blur.
export const FieldDebounceZod = z.union([z.number().int().nonnegative(), z.literal('blur')]);

export type FieldDebounce = z.infer<typeof FieldDebounceZod>;

export const EditorActionSchemaZod = z.object({
  action: z.string().min(1),
  placement: z.enum(['suffix', 'below']),
  label: z.string().min(1),
  icon: z.string().min(1).optional(),
}).strict();

export type EditorActionSchema = z.infer<typeof EditorActionSchemaZod>;

/**
 * How a field's value is drawn when it is not a plain scalar on a labelled row.
 * `relation-list` stacks the label above one brand-blue external-open link per record.
 */
export const FieldPresentation = {
  RelationList: 'relation-list',
} as const;

export type FieldPresentation = typeof FieldPresentation[keyof typeof FieldPresentation];

export const FieldPresentationZod: z.ZodType<FieldPresentation> = z.enum([FieldPresentation.RelationList]);

export const FieldSchemaZod = z.object({
  id: z.string().min(1),
  bind: z.string().min(1),
  metadata: JsonSafeMetadataZod.optional(),
  // Two shapes:
  //   - string: a SurfaceRuntime `editorInputs` factory ref key (resolved +
  //     ref-validated through the runtime chain).
  //   - object: an INLINE static base merged UNDER the factory output (factory
  //     wins per key) into the resolved editor-inputs signal. Lets simple
  //     inputs (e.g. `{ showMap: true }`) be schema-expressible without a
  //     registered factory. See runtime/editor.inputs merge order.
  editorInputs: z.union([z.string(), z.record(z.string(), JsonValueZod)]).optional(),
  editorActions: z.array(EditorActionSchemaZod).optional(),
  fieldRenderer: z.string().optional(),
  /** Native value presentation; omit for the default labelled-row value. */
  presentation: FieldPresentationZod.optional(),
  validators: z.array(ValidatorRefZod).optional(),
  required: z.boolean().optional(),
  colspan: z.number().int().positive().optional(),
  systemHidden: z.boolean().optional(),
  autofocus: z.boolean().optional(),
  debounce: FieldDebounceZod.optional(),
}).strict();

export type FieldSchema = z.infer<typeof FieldSchemaZod>;
